/* ==========================================================================
   Blotter ↔ ledger reconciliation, and honest words for a fill-capture run.

   WHY THIS EXISTS (2026-09-16, CIO report: "the fixed income trades showed up in the
   live blotter, but we didn't have to make any ledger note, nor did they show up there").

   The live blotter reads IBKR's ORDERS feed. The ledger is fed only by the EXECUTIONS
   feed, through fill capture. From 2026-08-21 that capture stored nothing -- IBKR answers
   the first executions read after an account switch with an empty list -- and every
   surface on this page read as "all clear": the break queue was empty because nothing
   reached it, and Sync fills printed a green "Broker feed read."

   Two things make that failure impossible to miss again:
     1. Every FILLED blotter order is reconciled against the ledger and says which state
        it is in. A trade the broker shows and the ledger does not hold is a finding.
     2. A capture report is described by what it could and could not read, never by
        whether the HTTP call returned 200.
   ========================================================================== */

/** The US market session a timestamp belongs to (YYYY-MM-DD, America/New_York).
 *
 * Must mirror the backend's _eastern_date (record_discretionary_trade, and the ticket
 * disposition's EntryDate), or a trade and its ledger entry could land on different
 * sessions: dating from UTC pushes anything after ~20:00 ET onto the following day. */
export const easternSession = (iso?: string | null): string => {
  if (!iso) return "undated";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
};

/** Broker side -> ledger direction. Mirrors fill_capture.fill_direction.
 *  'X' is an exchange/transfer marker, not a directional trade, and returns null so
 *  such records can never be gathered into a buy or a sell. Orders use BUY/SELL,
 *  executions B/S; both start with the letter that matters. ASSN/EXER return null. */
export const fillDirection = (side?: string | null): "BUY" | "SELL" | null => {
  const c = String(side || "").trim().toUpperCase().charAt(0);
  return c === "B" ? "BUY" : c === "S" ? "SELL" : null;
};

export type BlotterOrderLite = {
  orderId?: string | number | null;
  conid?: string | number | null;
  symbol?: string | null;
  side?: string | null;
  secType?: string | null;
  filled?: number | string | null;
  account?: string | null;
  lastExecutionTime_r?: number | string | null;
};

export type QueueGroupLite = {
  key: string;
  contractId: string;
  direction: "BUY" | "SELL";
  sessionDate: string;
  account: string | null;
};

export type LedgerEntryLite = {
  contractId?: string | null;
  side?: string | null;
  entryDate?: string | null;
  status?: string | null;
};

export type TicketLite = {
  orderId: number;
  contractId?: string | null;
  action?: string | null;
  symbol?: string | null;
};

export type BlotterLedgerStatus =
  /** A ledger entry with a reason exists for this contract, direction and session. */
  | { kind: "recorded" }
  /** Captured executions sit in the break queue with no reason. */
  | { kind: "needs_reason"; groupKey: string; ticketOrderId: number | null }
  /** The broker shows the fill; the ledger has neither an entry nor the executions. */
  | { kind: "not_captured" }
  /** The ledger or its queue could not be read. Make no claim either way. */
  | { kind: "unknown" };

/** True when two account labels can be compared. The ORDERS feed carries the IBKR account
 *  id ("U1234567"); the EXECUTIONS feed was seen in production on 2026-09-16 to carry an
 *  account ALIAS ending in "Fund" instead. Comparing an id with an alias would call every
 *  captured trade "not in ledger". Only two ids are compared; anything else is not evidence
 *  of a different account.
 *
 *  Since 2026-09-21 the API sends ids MASKED ("****7661", strategy/account_mask.py), while a
 *  row stored earlier may still hold the full id. Both forms compare on their last four digits,
 *  so a masked id and the full id of the same account agree. (Two accounts sharing their last
 *  four digits would read as the same; the fund's two accounts, ...7661 and ...0529, do not.) */
const IBKR_ACCOUNT_ID = /^[A-Z]{1,3}\d{5,}$/i;
const MASKED_ACCOUNT_ID = /^\*{2,}\d{4}$/;
const accountKey = (v?: string | null): string | null => {
  const s = String(v || "").trim();
  return IBKR_ACCOUNT_ID.test(s) || MASKED_ACCOUNT_ID.test(s) ? s.slice(-4) : null;
};
export const accountsDiffer = (a?: string | null, b?: string | null): boolean => {
  const x = accountKey(a);
  const y = accountKey(b);
  return x !== null && y !== null && x !== y;
};

/** The open ticket this trade belongs to, if one exists. A ticketed trade is recorded
 *  from its ticket on the Decide tab; recording it again from the break queue would put
 *  the same trade on the ledger twice. Options tickets key on the conid; the ALT sleeve's
 *  ETF tickets key on the ticker, so both are tried. */
export const findOpenTicket = (
  tickets: TicketLite[],
  conid: string,
  symbol: string | null | undefined,
  direction: "BUY" | "SELL",
): TicketLite | null => {
  const sym = String(symbol || "").trim().toUpperCase();
  return (
    tickets.find((t) => {
      if (fillDirection(t.action) !== direction) return false;
      const id = String(t.contractId ?? "").trim();
      return id === conid || (sym.length > 0 && id.toUpperCase() === sym);
    }) || null
  );
};

export type ReconcileContext = {
  groups: QueueGroupLite[];
  entries: LedgerEntryLite[];
  tickets: TicketLite[];
  /** False while the ledger is loading, failed, or its break queue is unreadable. */
  ledgerReadable: boolean;
  /** Fallback trade time when an order carries no execution time (the payload's as-of). */
  asOfUtc?: string | null;
};

/** Where a FILLED blotter order stands in the ledger. Null for an order with nothing
 *  filled, no direction, or no contract -- those are not trades to reconcile. */
export const reconcileBlotterOrder = (
  order: BlotterOrderLite,
  ctx: ReconcileContext,
): BlotterLedgerStatus | null => {
  const filled = Number(order.filled);
  if (!Number.isFinite(filled) || filled <= 0) return null;
  const direction = fillDirection(order.side);
  if (!direction) return null;
  const conid = String(order.conid ?? "").trim();
  if (!conid) return null;
  // A combo (BAG) order carries the combo's contract id while its executions and ledger
  // entries carry the legs'. It can never match here; its legs reach the queue on their own.
  // Mirrors fill_capture.find_feed_gaps.
  if (String(order.secType || "").toUpperCase() === "BAG") return null;
  if (!ctx.ledgerReadable) return { kind: "unknown" };

  const ms = Number(order.lastExecutionTime_r);
  const when = Number.isFinite(ms) && ms > 0 ? new Date(ms).toISOString() : ctx.asOfUtc ?? null;
  const session = easternSession(when);
  const sym = String(order.symbol || "").trim().toUpperCase();

  const group = ctx.groups.find(
    (g) =>
      g.contractId === conid &&
      g.direction === direction &&
      g.sessionDate === session &&
      !accountsDiffer(g.account, order.account),
  );
  if (group) {
    const ticket = findOpenTicket(ctx.tickets, conid, order.symbol, direction);
    return { kind: "needs_reason", groupKey: group.key, ticketOrderId: ticket ? ticket.orderId : null };
  }

  const entry = ctx.entries.find((e) => {
    const id = String(e.contractId ?? "").trim();
    return (
      (id === conid || (sym.length > 0 && id.toUpperCase() === sym)) &&
      fillDirection(e.side) === direction &&
      e.entryDate === session &&
      String(e.status || "").toUpperCase() === "EXECUTED"
    );
  });
  if (entry) return { kind: "recorded" };

  return { kind: "not_captured" };
};

/** Identity of a filled order for auto-capture bookkeeping. Includes the filled
 *  quantity, so a further partial fill on the same order is a new thing to capture. */
export const blotterFillKey = (order: BlotterOrderLite): string =>
  `${order.orderId ?? `${order.conid}-${order.side}`}|${Number(order.filled) || 0}`;

/** Auto-capture spacing and budget. Each capture re-reads IBKR's execution feed with
 *  5-second pacing per sub-account, so it is not cheap and shares the dashboard's IBKR
 *  session. Three tries per filled order, 90 s apart, then the tag tells the CIO to
 *  press Sync fills instead of retrying forever. */
export const AUTO_CAPTURE_SPACING_MS = 90_000;
export const AUTO_CAPTURE_MAX_ATTEMPTS = 3;

export const shouldAutoCapture = (args: {
  notCapturedKeys: string[];
  attempts: Record<string, number>;
  lastAutoCaptureAt: number | null;
  now: number;
  capturing: boolean;
  ledgerReadable: boolean;
}): boolean => {
  if (args.capturing || !args.ledgerReadable) return false;
  const due = args.notCapturedKeys.some((k) => (args.attempts[k] || 0) < AUTO_CAPTURE_MAX_ATTEMPTS);
  if (!due) return false;
  return args.lastAutoCaptureAt == null || args.now - args.lastAutoCaptureAt >= AUTO_CAPTURE_SPACING_MS;
};

export type CaptureGap = {
  symbol?: string | null;
  side?: string | null;
  orderFilled?: number | null;
  executionsFound?: number | null;
};

export type CaptureReport = {
  feed_status?: "ok" | "empty" | "incomplete" | string;
  feed_gaps?: CaptureGap[];
  feed_reads?: { account?: string; reads?: number | null; executions?: number | null; error?: boolean }[];
  executions_returned?: number;
  captured?: number;
  already_known?: number;
  ledger_entries_priced?: number;
  unmatched?: unknown[];
  errors?: string[];
};

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Words for a capture run. The tone follows what the run could READ, not the HTTP code:
 *  a 200 that read nothing is not a success. */
export const describeCapture = (
  r: CaptureReport,
): { tone: "success" | "warning" | "error"; text: string } => {
  const priced = Number(r.ledger_entries_priced ?? 0);
  const unmatched = Array.isArray(r.unmatched) ? r.unmatched.length : 0;
  const captured = Number(r.captured ?? 0);
  const known = Number(r.already_known ?? 0);
  const returned = Number(r.executions_returned ?? captured + known);
  const tail =
    (priced ? ` ${plural(priced, "ledger entry", "ledger entries")} priced.` : "") +
    (unmatched ? ` ${plural(unmatched, "execution")} still need a reason recorded.` : "");

  if (r.feed_status === "incomplete") {
    const gaps = Array.isArray(r.feed_gaps) ? r.feed_gaps : [];
    if (gaps.length) {
      const list = gaps
        .map(
          (g) =>
            `${g.symbol || "?"} ${g.side || ""} ${Number(g.orderFilled ?? 0).toLocaleString()} filled, ` +
            `${Number(g.executionsFound ?? 0).toLocaleString()} returned`,
        )
        .join("; ");
      return {
        tone: "error",
        text:
          `IBKR's execution feed did not return every fill the order list shows (${list}). ` +
          `Those trades are NOT in the ledger yet. Press Sync fills again in a minute; if this persists, capture is failing.` +
          tail,
      };
    }
    const errs = Array.isArray(r.errors) && r.errors.length ? ` (${r.errors.join(", ")})` : "";
    return {
      tone: "error",
      text:
        `Could not read IBKR's execution feed for every fund sub-account${errs}. ` +
        `Trades may be missing from the ledger. This is not an all-clear.` +
        tail,
    };
  }

  if (r.feed_status === "empty") {
    const reads = (r.feed_reads || []).map((x) => x.reads).filter((n): n is number => typeof n === "number");
    const readNote = reads.length ? ` after ${Math.max(...reads)} reads per account` : "";
    return {
      tone: "warning",
      text:
        `IBKR returned no executions for the last 7 days${readNote}. ` +
        `If you traded this week, those trades are NOT in the ledger. Press Sync fills again.` +
        tail,
    };
  }

  if (r.feed_status === "busy") {
    return {
      tone: "warning",
      text:
        "Another fill capture was already reading IBKR, so this one read nothing and did not interfere. " +
        "What it finds lands in the ledger within a minute; press Refresh or Sync fills again then." +
        tail,
    };
  }

  if (r.feed_status === "ok") {
    return {
      tone: "success",
      text:
        `Broker feed read: ${plural(returned, "execution")} from the last 7 days, ${captured.toLocaleString()} new.` +
        tail,
    };
  }

  // A backend from before 2026-09-16 reports no feed_status. It cannot tell an empty
  // feed from a quiet week, so neither can we: never call it a success.
  return {
    tone: captured || known ? "success" : "warning",
    text:
      captured || known
        ? `Broker feed read: ${captured.toLocaleString()} new, ${known.toLocaleString()} already recorded.${tail}`
        : `Broker feed returned no executions. That can mean no trades in 7 days or a feed that did not answer.${tail}`,
  };
};

/** A calendar date ("2026-08-21") as "21 Aug 2026". Formatted in UTC on purpose: the
 *  page's fmtLocalDate parses a bare date as UTC midnight and then renders it in the
 *  viewer's zone, which prints 20 Aug for the 21st anywhere west of Greenwich. */
export const fmtCalendarDate = (ymd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || "").trim());
  if (!m) return String(ymd || "—");
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" }).format(d);
};

/** Dates for which the ledger is known to be missing trades because capture was broken and
 *  IBKR's executions feed no longer holds them. Shown on the Ledger tab while set.
 *
 *  CLOSED by the CIO, 2026-09-16, without a backfill. The period was 2026-08-21 to 2026-09-14
 *  (capture stored nothing from 08-21; the recovery capture returned only 09-15). The CIO's
 *  reason for every trade in that period: "Putting capital to work from new LPs". Those trades
 *  have no ledger entries; the decision and reason are recorded in PROJECT_HUB.md. Set this again
 *  (from/to, YYYY-MM-DD) if a future capture gap needs to be shown. */
export const LEDGER_CAPTURE_GAP: { from: string; to: string } | null = null;
